/**
 * AI Agent (bot) campaign fields — stacked white tables for create / edit.
 * Sections: Campaign Information · Leads Selection · Dialing · Routing · AMD Detection
 */
import { useEffect, useMemo, useState } from "react";
import { CaretUpDown, Check } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  normalizeCallRecording,
  selectedOpensipsIds,
  selectedMediaServerIds,
} from "@/lib/campaignForm";
import FailoverServerMultiSelect from "@/components/FailoverServerMultiSelect";
import DialingScheduleFields from "@/components/DialingScheduleFields";
import CampaignDispositionFields from "@/components/CampaignDispositionFields";

const LEAD_STATUS_CODES = [
  { code: "NEW", label: "New" },
  { code: "QUEUE", label: "Queue" },
  { code: "DIALING", label: "Dialing" },
];

function Field({ label, required, hint, children, className = "", full = false }) {
  return (
    <div className={`space-y-2 ${full ? "sm:col-span-2" : ""} ${className}`}>
      {label && (
        <Label>
          {label}
          {required && <span className="text-destructive"> *</span>}
        </Label>
      )}
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** White card section matching the Predictive edit outline. */
function WhiteTable({ title, testid, children, cols = true }) {
  return (
    <section
      className="rounded-lg border border-border overflow-hidden bg-white shadow-sm"
      data-testid={testid}
    >
      <div className="border-b border-border bg-white px-4 py-2.5">
        <h3 className="text-sm font-semibold leading-none text-foreground">{title}</h3>
      </div>
      <div className={`bg-white p-4 ${cols ? "grid gap-4 sm:grid-cols-2" : "space-y-4"}`}>
        {children}
      </div>
    </section>
  );
}

function SmartMultiSelect({
  items,
  selectedIds,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No options",
  testid,
  getLabel = (item) => item.name || item.label || item.id,
  getId = (item) => item.id,
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => (Array.isArray(selectedIds) ? selectedIds : []),
    [selectedIds],
  );
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const byId = useMemo(() => {
    const m = new Map();
    (items || []).forEach((it) => m.set(getId(it), it));
    return m;
  }, [items, getId]);

  const display = useMemo(() => {
    if (!selected.length) return placeholder;
    const names = selected.map((id) => getLabel(byId.get(id) || { id }));
    if (names.length <= 2) return names.join(", ");
    return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
  }, [selected, byId, placeholder, getLabel]);

  const toggle = (id) => {
    if (selectedSet.has(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="w-full justify-between font-normal h-9 px-3 bg-white"
        >
          <span className="truncate">{display}</span>
          <CaretUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {(items || []).map((it) => {
                const id = getId(it);
                const on = selectedSet.has(id);
                return (
                  <CommandItem
                    key={id}
                    value={`${getLabel(it)} ${id}`}
                    data-testid={`${testid}-option-${id}`}
                    onSelect={() => toggle(id)}
                  >
                    <Check className={`mr-2 h-3.5 w-3.5 shrink-0 ${on ? "opacity-100" : "opacity-0"}`} />
                    <span className="truncate">{getLabel(it)}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function tid(prefix, name) {
  return `${prefix}-${name}`;
}

/**
 * @param {"edit"|"create"} mode — testid prefix edit-ai-agent or create-ai-agent
 */
export default function AiAgentCampaignFields({
  mode = "edit",
  value,
  onChange,
  options = {},
  optionsError: _optionsError,
  vmUploading = false,
  vmInputRef,
  onVmUploadClick,
  onVmFileChange,
}) {
  const prefix = mode === "edit" ? "edit-ai-agent" : "create-ai-agent";
  const v = value || {};
  const set = (patch) => onChange(patch);
  const [dispositions, setDispositions] = useState([]);
  const [disposLoading, setDisposLoading] = useState(false);

  const {
    pdServers = [],
    assigned = [],
    mediaServers = [],
    leadLists = [],
    vendors = [],
    timezones = [],
  } = options;
  const opensipsIds = selectedOpensipsIds(v);
  const mediaIds = selectedMediaServerIds(v);
  const recordingOn = normalizeCallRecording(v.callRecording, v.recordingEnabled) === "always";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDisposLoading(true);
      try {
        const { data } = await api.get("/customer-settings/dispositions");
        if (cancelled) return;
        const system = Array.isArray(data?.system) ? data.system : [];
        const custom = Array.isArray(data?.custom) ? data.custom : [];
        const seen = new Set();
        const rows = [];
        for (const row of [...LEAD_STATUS_CODES, ...system, ...custom]) {
          const code = String(row.code || "").toUpperCase();
          if (!code || code === "DNC" || seen.has(code)) continue;
          seen.add(code);
          rows.push({
            code,
            label: row.label || code,
            source: row.source || (LEAD_STATUS_CODES.some((x) => x.code === code) ? "lead" : "system"),
          });
        }
        setDispositions(rows);
      } catch {
        if (!cancelled) {
          setDispositions(
            LEAD_STATUS_CODES.concat([
              { code: "ANSWERED", label: "Answered" },
              { code: "BUSY", label: "Busy" },
              { code: "NOANSWER", label: "No Answer" },
              { code: "FAILED", label: "Failed" },
              { code: "COMPLETED", label: "Completed" },
              { code: "CALLBACK", label: "Callback" },
            ]),
          );
        }
      } finally {
        if (!cancelled) setDisposLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleDialStatus = (code, checked) => {
    const cur = Array.isArray(v.dialStatuses) ? v.dialStatuses : [];
    set({
      dialStatuses: checked
        ? (cur.includes(code) ? cur : [...cur, code])
        : cur.filter((x) => x !== code),
    });
  };

  return (
    <div className="space-y-4" data-testid={tid(prefix, "form")}>
      {/* ── Campaign Information ─────────────────────────────── */}
      <WhiteTable title="Campaign Information" testid={tid(prefix, "section-info")} cols={false}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Campaign name" required>
            <Input
              data-testid={tid(prefix, "name")}
              className="bg-white"
              value={v.name || ""}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="e.g. Sales Outbound"
            />
          </Field>
          <Field
            label="Dial Rate"
            required
            hint="Calls per second (CPS)."
          >
            <Input
              data-testid={tid(prefix, "dial-rate")}
              className="bg-white"
              type="number"
              min="0.1"
              step="0.1"
              required
              value={v.dialRate ?? "1"}
              onChange={(e) => set({ dialRate: e.target.value })}
              placeholder="1"
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 items-end">
          <Field label="Call recording">
            <div className="flex h-9 items-center justify-between gap-4 rounded-md border bg-white px-3">
              <span className="text-xs text-muted-foreground truncate">
                Record when the customer is connected
              </span>
              <Switch
                data-testid={tid(prefix, "recording")}
                checked={recordingOn}
                onCheckedChange={(on) => set({
                  callRecording: on ? "always" : "never",
                  ...(on ? {} : { recordingStartDelaySec: "" }),
                })}
              />
            </div>
          </Field>
          <Field
            label="Start Recording after (sec)"
            hint="Leave empty to start as soon as the call is connected."
          >
            <Input
              data-testid={tid(prefix, "recording-delay")}
              className="bg-white"
              type="number"
              min="1"
              max="3600"
              disabled={!recordingOn}
              value={recordingOn ? (v.recordingStartDelaySec || "") : ""}
              onChange={(e) => set({ recordingStartDelaySec: e.target.value })}
              placeholder={recordingOn ? "" : "—"}
            />
          </Field>
        </div>

        <Field
          label="Max Call Duration (sec)"
          hint="Caps the AI bot ↔ customer call. Leave blank for no limit. Not applied after transfer to an agent."
        >
          <Input
            data-testid={tid(prefix, "max-duration")}
            className="bg-white"
            type="number"
            min="1"
            max="3600"
            value={v.maxDur || ""}
            onChange={(e) => set({ maxDur: e.target.value })}
            placeholder="Unlimited"
          />
        </Field>
      </WhiteTable>

      {/* ── Leads Selection ──────────────────────────────────── */}
      <WhiteTable title="Leads Selection" testid={tid(prefix, "section-leads")} cols={false}>
        <Field label="Select Lists" hint="Lead lists this campaign may dial.">
          <SmartMultiSelect
            items={leadLists}
            selectedIds={v.listIds || []}
            onChange={(ids) => set({ listIds: ids })}
            placeholder="Select list(s)…"
            searchPlaceholder="Search lists…"
            emptyText="No lists yet — create them under Lists."
            testid={tid(prefix, "lists")}
            getLabel={(l) => (
              l.lead_count != null ? `${l.name} (${l.lead_count})` : (l.name || l.id)
            )}
          />
        </Field>

        <CampaignDispositionFields
          value={v}
          onChange={set}
          testidPrefix={tid(prefix, "dispos")}
        />
      </WhiteTable>

      {/* ── Dialing ──────────────────────────────────────────── */}
      <WhiteTable title="Dialing" testid={tid(prefix, "section-dialing")} cols={false}>
        <DialingScheduleFields
          value={v}
          onChange={set}
          timezones={timezones}
          testidPrefix={tid(prefix, "dialing")}
        />
      </WhiteTable>

      {/* ── Routing ──────────────────────────────────────────── */}
      <WhiteTable title="Routing" testid={tid(prefix, "section-routing")} cols={false}>
        <Field
          label="Select PD servers"
          required={pdServers.length !== 1}
          hint="First selected is Primary; others are backups if the primary is down or at capacity."
        >
          <FailoverServerMultiSelect
            servers={pdServers}
            selectedIds={
              (v.pdServerIds || []).length
                ? v.pdServerIds
                : (v.pdServerId ? [v.pdServerId] : [])
            }
            onChange={(ids) => set({ pdServerIds: ids, pdServerId: ids[0] || "" })}
            testid={tid(prefix, "pd-servers")}
            emptyText="No PD servers assigned to this account."
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Telephony IP"
            required
            hint="First selected is Primary; others are backups if the primary is down or at capacity."
          >
            <FailoverServerMultiSelect
              servers={assigned}
              selectedIds={opensipsIds}
              onChange={(ids) => set({ opensipsIds: ids, opensipsId: ids[0] || "__none__" })}
              testid={tid(prefix, "telephony-ip")}
              emptyText="No Telephony IP servers assigned to this account."
            />
          </Field>
          <Field
            label="Media server"
            required
            hint="First selected is Primary; others are backups if the primary is down or at capacity."
          >
            <FailoverServerMultiSelect
              servers={mediaServers}
              selectedIds={mediaIds}
              onChange={(ids) => set({ mediaServerIds: ids, mediaServerId: ids[0] || "" })}
              testid={tid(prefix, "media-servers")}
              emptyText="No media servers assigned to this account."
            />
          </Field>
        </div>

        <Field
          label="Vendor route"
          required
          hint="Required. Campaign-selected vendor only — no outbound vendor failover."
        >
          <Select
            value={v.vendorId && v.vendorId !== "__none__" ? v.vendorId : undefined}
            onValueChange={(x) => set({ vendorId: x })}
          >
            <SelectTrigger data-testid={tid(prefix, "vendor")} className="bg-white">
              <SelectValue placeholder="Select vendor route…" />
            </SelectTrigger>
            <SelectContent>
              {vendors.map((x) => (
                <SelectItem key={x.id} value={x.id}>{x.name} · {x.host}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {vendors.length === 0 && (
            <p className="text-xs text-destructive" data-testid={tid(prefix, "vendor-empty")}>
              No active vendor routes — create one under Routing → Vendors.
            </p>
          )}
        </Field>
      </WhiteTable>

      {/* ── AMD Detection ───────────────────────────────────── */}
      <WhiteTable title="AMD Detection" testid={tid(prefix, "section-amd")} cols={false}>
        <div className="flex items-center justify-between gap-4 rounded-md border bg-white px-3 py-2">
          <div>
            <Label className="text-sm">AMD Detection</Label>
            <p className="text-[11px] text-muted-foreground">
              Classify human vs answering machine on connect.
            </p>
          </div>
          <Switch
            data-testid={tid(prefix, "amd-enabled")}
            checked={!!v.amdEnabled}
            onCheckedChange={(x) => set({ amdEnabled: x })}
          />
        </div>

        {v.amdEnabled && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Decision window (ms)">
              <Input
                data-testid={tid(prefix, "amd-timeout")}
                className="bg-white"
                type="number"
                min="1000"
                max="8000"
                step="100"
                value={v.amdTimeout || "3200"}
                onChange={(e) => set({ amdTimeout: e.target.value })}
              />
            </Field>

            <Field label="On voicemail">
              <Select
                value={
                  v.amdAction === "voicemail" || v.amdAction === "log_only"
                    ? v.amdAction
                    : "hangup"
                }
                onValueChange={(x) => set({ amdAction: x })}
              >
                <SelectTrigger data-testid={tid(prefix, "amd-action")} className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hangup">Hangup</SelectItem>
                  <SelectItem value="voicemail">Leave voicemail</SelectItem>
                  <SelectItem value="log_only">Log only</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Voicemail Max (ms)">
              <Input
                data-testid={tid(prefix, "amd-greeting-max")}
                className="bg-white"
                type="number"
                min="2000"
                max="12000"
                step="100"
                value={v.amdGreetingMax || "4500"}
                onChange={(e) => set({ amdGreetingMax: e.target.value })}
              />
            </Field>

            {v.amdAction === "voicemail" && (
              <Field label="Voicemail drop audio" required className="sm:col-span-2">
                <input
                  ref={vmInputRef}
                  type="file"
                  accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.webm"
                  hidden
                  onChange={onVmFileChange}
                  data-testid={tid(prefix, "vm-file-input")}
                />
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="bg-white"
                    disabled={vmUploading}
                    onClick={onVmUploadClick}
                    data-testid={tid(prefix, "vm-upload")}
                  >
                    {vmUploading ? "Uploading…" : (v.vmAudioId ? "Replace audio" : "Upload audio")}
                  </Button>
                  {v.vmAudioId ? (
                    <span className="text-xs text-emerald-600 truncate" data-testid={tid(prefix, "vm-filename")}>
                      {v.vmFilename || "Voicemail audio attached"}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">No file uploaded yet</span>
                  )}
                </div>
              </Field>
            )}
          </div>
        )}
      </WhiteTable>
    </div>
  );
}
